import { NODE_AUTO_COMPLETE_RULE_ENDPOINTS } from '../endpoints/nodeAutoCompleteRule';
import { getAuthHeaders, handleResponse } from '../client';
import type {
  NodeAutoCompleteRulePagedResultVO,
  NodeAutoCompleteRuleSaveDTO,
  NodeAutoCompleteRuleScriptTestDTO,
  NodeAutoCompleteRuleTestDTO,
  NodeAutoCompleteRuleTestProjectPagedVO,
  NodeAutoCompleteRuleTestResultVO,
  NodeAutoCompleteRuleRunLogPagedVO,
  NodeAutoCompleteRuleUpdateDTO,
  NodeAutoCompleteRuleVersionPagedVO,
  NodeAutoCompleteRuleVersionVO,
  NodeAutoCompleteRuleVO,
  NodeAutoCompleteRuleTriggerResultVO,
  NodeAutoCompleteRuleStepTestResultVO,
} from '../types/nodeAutoCompleteRule';

export const nodeAutoCompleteRuleApi = {
  getList: async (params?: {
    name?: string;
    enabled?: number;
    pageNum?: number;
    pageSize?: number;
  }): Promise<NodeAutoCompleteRulePagedResultVO> => {
    const queryParams = new URLSearchParams();
    if (params?.name) queryParams.append('Name', params.name);
    if (params?.enabled != null) queryParams.append('Enabled', String(params.enabled));
    queryParams.append('PageNum', String(params?.pageNum ?? 1));
    queryParams.append('PageSize', String(params?.pageSize ?? 20));

    const response = await fetch(`${NODE_AUTO_COMPLETE_RULE_ENDPOINTS.LIST}?${queryParams}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<NodeAutoCompleteRulePagedResultVO>(response);
  },

  getById: async (id: number): Promise<NodeAutoCompleteRuleVO> => {
    const response = await fetch(NODE_AUTO_COMPLETE_RULE_ENDPOINTS.DETAIL(id), {
      headers: getAuthHeaders(),
    });
    return handleResponse<NodeAutoCompleteRuleVO>(response);
  },

  create: async (data: NodeAutoCompleteRuleSaveDTO): Promise<NodeAutoCompleteRuleVO> => {
    const response = await fetch(NODE_AUTO_COMPLETE_RULE_ENDPOINTS.CREATE, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<NodeAutoCompleteRuleVO>(response);
  },

  update: async (id: number, data: NodeAutoCompleteRuleUpdateDTO): Promise<NodeAutoCompleteRuleVO> => {
    const response = await fetch(NODE_AUTO_COMPLETE_RULE_ENDPOINTS.UPDATE(id), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<NodeAutoCompleteRuleVO>(response);
  },

  delete: async (id: number): Promise<void> => {
    const response = await fetch(NODE_AUTO_COMPLETE_RULE_ENDPOINTS.DELETE(id), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleResponse<unknown>(response);
  },

  setEnabled: async (id: number, enabled: boolean): Promise<NodeAutoCompleteRuleVO> => {
    const response = await fetch(NODE_AUTO_COMPLETE_RULE_ENDPOINTS.SET_ENABLED(id), {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ enabled }),
    });
    return handleResponse<NodeAutoCompleteRuleVO>(response);
  },

  test: async (data: NodeAutoCompleteRuleTestDTO): Promise<NodeAutoCompleteRuleTestResultVO> => {
    const response = await fetch(NODE_AUTO_COMPLETE_RULE_ENDPOINTS.TEST, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<NodeAutoCompleteRuleTestResultVO>(response);
  },

  testScriptStep: async (
    data: NodeAutoCompleteRuleScriptTestDTO,
  ): Promise<NodeAutoCompleteRuleStepTestResultVO> => {
    const response = await fetch(NODE_AUTO_COMPLETE_RULE_ENDPOINTS.TEST_SCRIPT, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        step: data.step,
        script: data.script,
        context: data.context,
        timeoutSec: data.timeoutSec,
      }),
    });
    return handleResponse<NodeAutoCompleteRuleStepTestResultVO>(response);
  },

  getTestProjects: async (
    ruleId: number,
    params?: { name?: string; pageNum?: number; pageSize?: number },
  ): Promise<NodeAutoCompleteRuleTestProjectPagedVO> => {
    const queryParams = new URLSearchParams();
    if (params?.name) queryParams.append('Name', params.name);
    queryParams.append('PageNum', String(params?.pageNum ?? 1));
    queryParams.append('PageSize', String(params?.pageSize ?? 50));

    const response = await fetch(
      `${NODE_AUTO_COMPLETE_RULE_ENDPOINTS.TEST_PROJECTS(ruleId)}?${queryParams}`,
      { headers: getAuthHeaders() },
    );
    return handleResponse<NodeAutoCompleteRuleTestProjectPagedVO>(response);
  },

  getRuns: async (
    ruleId: number,
    params?: { pageNum?: number; pageSize?: number },
  ): Promise<NodeAutoCompleteRuleRunLogPagedVO> => {
    const queryParams = new URLSearchParams();
    queryParams.append('PageNum', String(params?.pageNum ?? 1));
    queryParams.append('PageSize', String(params?.pageSize ?? 20));

    const response = await fetch(
      `${NODE_AUTO_COMPLETE_RULE_ENDPOINTS.RUNS(ruleId)}?${queryParams}`,
      { headers: getAuthHeaders() },
    );
    return handleResponse<NodeAutoCompleteRuleRunLogPagedVO>(response);
  },

  getVersions: async (
    ruleId: number,
    params?: { pageNum?: number; pageSize?: number },
  ): Promise<NodeAutoCompleteRuleVersionPagedVO> => {
    const queryParams = new URLSearchParams();
    queryParams.append('PageNum', String(params?.pageNum ?? 1));
    queryParams.append('PageSize', String(params?.pageSize ?? 20));

    const response = await fetch(
      `${NODE_AUTO_COMPLETE_RULE_ENDPOINTS.VERSIONS(ruleId)}?${queryParams}`,
      { headers: getAuthHeaders() },
    );
    return handleResponse<NodeAutoCompleteRuleVersionPagedVO>(response);
  },

  getVersionById: async (ruleId: number, versionId: number): Promise<NodeAutoCompleteRuleVersionVO> => {
    const response = await fetch(
      NODE_AUTO_COMPLETE_RULE_ENDPOINTS.VERSION_DETAIL(ruleId, versionId),
      { headers: getAuthHeaders() },
    );
    return handleResponse<NodeAutoCompleteRuleVersionVO>(response);
  },

  /** 按接口 triggerKey 正式触发 */
  triggerByApi: async (
    triggerKey: string,
    triggerPayload: unknown,
  ): Promise<NodeAutoCompleteRuleTriggerResultVO> => {
    const query = new URLSearchParams();
    query.append('triggerKey', triggerKey);
    const response = await fetch(`${NODE_AUTO_COMPLETE_RULE_ENDPOINTS.TRIGGER}?${query}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(triggerPayload ?? {}),
    });
    return handleResponse<NodeAutoCompleteRuleTriggerResultVO>(response);
  },

  /** 按 MQ VHost + 队列正式触发（与监听器共用后端 TriggerByMqAsync） */
  triggerByMq: async (
    mqVhost: string,
    mqQueue: string,
    triggerPayload: unknown,
  ): Promise<NodeAutoCompleteRuleTriggerResultVO> => {
    const query = new URLSearchParams();
    query.append('mqVhost', mqVhost);
    query.append('mqQueue', mqQueue);
    const response = await fetch(`${NODE_AUTO_COMPLETE_RULE_ENDPOINTS.TRIGGER_MQ}?${query}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(triggerPayload ?? {}),
    });
    return handleResponse<NodeAutoCompleteRuleTriggerResultVO>(response);
  },
};
